import { Injectable } from '@angular/core';
import {
  AuthChangeEvent,
  Session,
  SignInWithPasswordCredentials,
  SignUpWithPasswordCredentials,
  User,
} from '@supabase/supabase-js';
import { BehaviorSubject } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';
import { SupabaseCore } from './supabase.core';

// Definir el tipo de sesión con estado ready
interface SessionState {
  ready: boolean;
  session: Session | null;
}

@Injectable({
  providedIn: 'root',
})
export class SupabaseSession {
  private _sessionState$ = new BehaviorSubject<SessionState>({
    ready: false,
    session: null,
  });

  private _supabaseCore: SupabaseCore | null = null;

  // Observable que solo emite cuando está listo
  public readonly session$ = this._sessionState$.pipe(
    filter((state) => state.ready === true), // ⭐ Solo cuando esté listo
    distinctUntilChanged((prev, curr) => prev.session === curr.session),
    map((state) => state.session) // Extraer solo la sesión
  );

  constructor(private supabaseCore: SupabaseCore) {
    this.initializeAuth();
  }

  /*private get supabaseCore(): SupabaseCore {
    if (!this._supabaseCore) {
      this._supabaseCore = inject(SupabaseCore);
      this.initializeAuth();
    }
    return this._supabaseCore;
  }*/

  private async initializeAuth() {
    const {
      data: { session },
    } = await this.supabaseCore.client.auth.getSession();

    // ⭐ Ahora marcamos como ready=true
    this._sessionState$.next({ ready: true, session });

    this.supabaseCore.client.auth.onAuthStateChange(
      (event: AuthChangeEvent, session: Session | null) => {
        this._sessionState$.next({ ready: true, session });
      }
    );
  }

  get currentSession(): Session | null {
    return this._sessionState$.value.session;
  }

  get currentUser(): User | null {
    return this.currentSession?.user ?? null;
  }

  async signInWithPassword(credentials: SignInWithPasswordCredentials) {
    return await this.supabaseCore.client.auth.signInWithPassword(credentials);
  }

  /**
   * Creates a new user account with email and password
   * @param credentials - The user credentials containing email and password
   * @returns Promise with the sign up response containing user data and session
   * @example
   * ```typescript
   * const { data, error } = await authService.signUpWithPassword({
   *   email: 'user@example.com',
   *   password: 'password123',
   *   options: {
   *     data: { username: 'john_doe' }
   *   }
   * });
   *
   * if (error) {
   *   console.error('Sign up failed:', error.message);
   * } else {
   *   console.log('User created:', data.user);
   * }
   * ```
   */
  async signUpWithPassword(credentials: SignUpWithPasswordCredentials) {
    return await this.supabaseCore.client.auth.signUp(credentials);
  }

  async signInWithOtp(email: string) {
    return await this.supabaseCore.client.auth.signInWithOtp({ email });
  }

  async sendMagicLink(email: string, lang: string = 'es') {
    return await this.supabaseCore.client.functions.invoke('fn-auth-magic-link', {
      body: { email, lang },
    });
  }

  async signOut() {
    return await this.supabaseCore.client.auth.signOut();
  }

  async resetPassword(email: string) {
    return await this.supabaseCore.client.auth.resetPasswordForEmail(email);
  }

  onAuthStateChange(callback: (event: AuthChangeEvent, session: Session | null) => void) {
    return this.supabaseCore.client.auth.onAuthStateChange(callback);
  }
}
